// Firebase Authentication, and nothing else.
//
// Note what is *not* imported here: firebase/firestore, firebase/storage,
// firebase/database. The browser has no read or write access to any of them --
// the rules deny everything and the API function is the only way in (see
// ARCHITECTURE.md section 2). Importing the Firestore SDK would not break that,
// but it would make it look reachable to the next person, so the dependency is
// left out entirely.
//
// The web config is fetched from /__/firebase/init.json, a file Firebase
// Hosting generates and serves for every project. That means no apiKey in the
// repo, no build-time env vars, and no chance of a build being pointed at the
// wrong project: whichever site served the page also served its own config.
// In `npm run dev` Vite proxies /__ to the deployed site (see vite.config.ts).

import { initializeApp, type FirebaseApp, type FirebaseOptions } from "firebase/app";
import {
  getAuth,
  browserLocalPersistence,
  setPersistence,
  type Auth
} from "firebase/auth";

let app: FirebaseApp | null = null;
let auth: Auth | null = null;

export const FIREBASE_CONFIG_URL = "/__/firebase/init.json";

export async function loadFirebaseConfig(
  fetchImpl: typeof fetch = fetch
): Promise<FirebaseOptions> {
  const response = await fetchImpl(FIREBASE_CONFIG_URL, { cache: "no-store" });
  if (!response.ok) {
    throw new Error(
      `Could not load the Firebase configuration (${response.status}). ` +
        "If you are running locally, the dev server proxies this to the deployed site."
    );
  }
  const config = (await response.json()) as FirebaseOptions;
  // A malformed init.json produces an auth failure several screens later that
  // looks like a login bug. Name the real problem here instead.
  if (!config || !config.apiKey || !config.projectId) {
    throw new Error("The Firebase configuration is missing apiKey or projectId.");
  }
  return config;
}

// Called once, from main.tsx, before anything renders. Everything downstream
// gets a ready Auth instance rather than having to handle "not initialized yet"
// as a third state alongside signed-in and signed-out.
export async function initFirebase(fetchImpl: typeof fetch = fetch): Promise<Auth> {
  if (auth) return auth;

  const config = await loadFirebaseConfig(fetchImpl);
  app = initializeApp(config);
  auth = getAuth(app);

  // Explicit rather than relying on the default, because the default has
  // changed across major versions and a dashboard that logs you out on refresh
  // is a bug report we would spend an afternoon on.
  await setPersistence(auth, browserLocalPersistence);
  return auth;
}

export function firebaseAuth(): Auth {
  if (!auth) throw new Error("initFirebase() has not finished yet");
  return auth;
}
